import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { EditorContent, generateHTML } from "@tiptap/react";
import { forwardRef, useEffect } from "react";
import { RiEyeLine, RiPencilLine, RiHistoryLine } from "react-icons/ri";
import { useAppState } from "../../../../components/App/AppProvider";
import {
  DeleteIcon,
  ExportIcon,
  MoreMenuIcon,
} from "../../../../constants/commonIcons";
import { hasAccessTo } from "../../../../shared/_core/limit.utils";
import { useApiMutation } from "../../../../utilities/apibelRequest";
import {
  formatDateOrIsoString,
  formatUtcDateReadableLong,
} from "../../../../utilities/dateUtils";
import {
  getEditorExtensions,
  useEditorWithExtensions,
} from "../utils/useEditorWithExtensions";

type Props = {
  title: string;
  manualTitle: string;
  contentJSON: string;
  sectionHTMLID: string;
  responsibleUser: {
    firstName?: string;
    lastName?: string;
    userID: string;
  };
  manualResponsibleUser: {
    firstName?: string;
    lastName?: string;
    userID: string;
  };
  modified: {
    date: string | null;
    firstName?: string;
    lastName?: string;
    userID: string;
  };
  onClickEdit: () => void;
  onClickDelete: () => void;
  onClickUsage: () => void;
  onClickHistory: () => void;
};

const ManualSection = forwardRef<HTMLDivElement, Props>(
  (
    {
      onClickDelete,
      responsibleUser,
      manualResponsibleUser,
      modified,
      title,
      manualTitle,
      contentJSON,
      sectionHTMLID,
      onClickEdit,
      onClickUsage,
      onClickHistory,
    },
    headingRef,
  ) => {
    const { permissions } = useAppState().app;
    const { app } = useAppState();
    const isOwn =
      responsibleUser.userID === permissions.INFO.userID ||
      manualResponsibleUser.userID === permissions.INFO.userID;

    // Deleting a section just uses the same permissions as editing, MANUAL.DELETE is used for deleting the manual itself
    const canEdit =
      permissions.MANUAL.UPDATE.ALL ||
      (permissions.MANUAL.UPDATE.SELF && isOwn);
    const canExportManualSection = hasAccessTo("export", app.license);
    const readonlyEditor = useEditorWithExtensions({
      content: contentJSON.length >= 1 ? JSON.parse(contentJSON) : undefined,
      editable: false,
      placeholder: "No content",
      limit: 20000,
    });
    const exportToPDF = useApiMutation("manual/exportToPDF");
    const exportToDOCX = useApiMutation("manual/exportToDOCX");
    const isMutating = exportToPDF.isPending || exportToDOCX.isPending;

    const exportSection = () => {
      const { newLogoPath, logoPath } = app.accountInfo;
      const editorExtensions = getEditorExtensions();
      let html;
      try {
        const parsedContent = contentJSON ? JSON.parse(contentJSON) : null;
        const htmlContent = parsedContent
          ? generateHTML(parsedContent, editorExtensions)
          : "<i>No content available for this section</i>";
        const lastModified = modified.date
          ? formatUtcDateReadableLong(new Date(modified.date))
          : "N/A";

        html = `
      <div class="section">
      <p class="section-meta"><strong>Manual:</strong> ${manualTitle}</p>
        <p class="section-meta"><strong>Last Modified:</strong> ${
          lastModified
        }</p>
        <p class="section-meta"><strong>Modified By:</strong> ${
          modified.firstName || ""
        } ${modified.lastName || ""}</p>
        <div class="section-content">${htmlContent}</div>
      </div>
    `;
      } catch (error) {
        console.error("Error parsing JSON:", error);
        html =
          "<div class='section-error'><i>Error loading section content</i></div>";
      }
      return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title || "Document"}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .content {
            flex: 1;
          }
          .header {
            display: flex;
            align-items: center;
            margin-bottom: 20px;
          }
          .header img {
            max-height: 72px;
            max-width: 100%;
            margin-right: 20px;
          }
          h1 {
            font-size: 32px;
            margin: 0;
          }
          .section {
            page-break-after: always;
            margin-bottom: 20px;
          }
          .section-header {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
          }
          .section-title {
            font-size: 24px;
            margin: 0;
          }
          .section-meta {
            font-size: 14px;
            color: #666;
            margin: 0 0 5px 0;
          }
          .section-content {
            margin-top: 10px;
          }
          .section-error {
            color: red;
            font-style: italic;
          }
          .footer {
            margin-top: auto;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
            page-break-inside: avoid;
          }
          
        </style>
      </head>
      <body>
        <div class="content">
          <div class="header">
            <img src="${newLogoPath || logoPath}" alt="Logo" />
            <h1>${title || "Untitled Document"}</h1>
          </div>
          ${html}
        </div>
      </body>
    </html>
    `;
    };

    const handleExportSectionPDF = async () => {
      const htmlStr = exportSection();
      console.log("Title value:", title); // Add this to debug
      const result = await exportToPDF.mutateAsync({
        htmlContent: htmlStr,
        fileName: title || "Manual Section",
      });
      const link = document.createElement("a");
      link.href = result.url || "";
      link.download = `${title || "ManualSection"}.pdf`;
      link.click();
    };
    const handleExportSectionDocx = async () => {
      const htmlStr = exportSection();
      const result = await exportToDOCX.mutateAsync({
        htmlContent: htmlStr,
        fileName: title || "Manual Section",
      });
      const link = document.createElement("a");
      link.href = result.url || "";
      link.download = `${title || "ManualSection"}.docx`;
      link.click();
    };

    useEffect(() => {
      if (readonlyEditor && readonlyEditor.commands && contentJSON) {
        const parsedContent =
          contentJSON.length >= 1 ? JSON.parse(contentJSON) : undefined;
        readonlyEditor.commands.setContent(parsedContent);
      }
    }, [contentJSON, readonlyEditor]);

    const { isOpen, onOpen, onClose } = useDisclosure();

    return (
      <Box
        minH="80px"
        maxW="container.lg"
        w="100%"
        px="1"
        pt="2"
        pb="2"
        _focusWithin={{
          ".hover-buttons": {
            visibility: "visible",
            opacity: 1,
          },
        }}
        _hover={{
          ".hover-buttons": {
            visibility: "visible",
            opacity: 1,
          },
        }}
        sx={{
          ".hover-buttons": isOpen
            ? {
                visibility: "visible !important",
                opacity: "1 !important",
              }
            : {},
        }}>
        <Box
          px="3"
          py="4"
          bg="white"
          zIndex={1}
          ref={headingRef}
          willChange="transform"
          scrollMargin="calc(var(--chakra-space-top-bar) + 2rem)"
          id={sectionHTMLID}>
          <HStack bg="white" w="100%" justify="space-between">
            <Box flex={1}>
              <VStack align="start" gap="1">
                <Heading as="h1" fontSize="2.5rem" fontWeight="semibold">
                  {title}
                </Heading>
                <VStack align="start" gap="0">
                  <Text fontSize="xs" color="gray.600">
                    Owned by {responsibleUser.firstName}{" "}
                    {responsibleUser.lastName}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Last updated on{" "}
                    {formatDateOrIsoString(modified.date, "datetime-short")} by{" "}
                    {modified.firstName} {modified.lastName}
                  </Text>
                </VStack>
              </VStack>
            </Box>
            <ButtonGroup
              size="sm"
              spacing={0}
              variant="ghost"
              visibility="hidden"
              opacity={0}
              className="hover-buttons">
              <Tooltip
                label={
                  !canEdit
                    ? "You don't have permission to edit this"
                    : undefined
                }>
                <Button
                  isLoading={isMutating}
                  isDisabled={!canEdit}
                  leftIcon={<RiPencilLine size="16" />}
                  onClick={onClickEdit}>
                  Edit
                </Button>
              </Tooltip>
              <Menu size="sm" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
                <MenuButton
                  hidden={isMutating}
                  as={IconButton}
                  icon={<MoreMenuIcon />}
                />
                <Portal>
                  <MenuList
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="sm"
                    py="1"
                    minW="180px">
                    <MenuItem
                      onClick={onClickUsage}
                      icon={<RiEyeLine />}
                      _hover={{
                        bg: "gray.100",
                      }}
                      border="none"
                      _focus={{
                        bg: "gray.100",
                      }}>
                      Usage
                    </MenuItem>
                    <MenuDivider />
                    <Tooltip
                      label={
                        !canEdit
                          ? "You don't have permission to view the history of this section"
                          : undefined
                      }>
                      <MenuItem
                        onClick={onClickHistory}
                        isDisabled={!canEdit}
                        icon={<RiHistoryLine />}
                        border="none"
                        _hover={{
                          bg: canEdit ? "gray.100" : "transparent",
                        }}
                        _focus={{
                          bg: canEdit ? "gray.100" : "transparent",
                        }}
                        _disabled={{
                          opacity: 0.4,
                          cursor: "not-allowed",
                        }}>
                        View History
                      </MenuItem>
                    </Tooltip>
                    <MenuDivider />
                    <Tooltip
                      label={
                        !canExportManualSection
                          ? "Upgrade your license to export this section"
                          : undefined
                      }>
                      <MenuItem
                        onClick={handleExportSectionPDF}
                        isDisabled={!canExportManualSection}
                        icon={<ExportIcon />}
                        border="none"
                        _hover={{
                          bg: canExportManualSection
                            ? "gray.100"
                            : "transparent",
                        }}
                        _focus={{
                          bg: canExportManualSection
                            ? "gray.100"
                            : "transparent",
                        }}
                        _disabled={{
                          opacity: 0.4,
                          cursor: "not-allowed",
                        }}>
                        Export as PDF
                      </MenuItem>
                    </Tooltip>
                    <Tooltip
                      label={
                        !canExportManualSection
                          ? "Upgrade your license to export this section"
                          : undefined
                      }>
                      <MenuItem
                        onClick={handleExportSectionDocx}
                        isDisabled={!canExportManualSection}
                        icon={<ExportIcon />}
                        border="none"
                        _hover={{
                          bg: canExportManualSection
                            ? "gray.100"
                            : "transparent",
                        }}
                        _focus={{
                          bg: canExportManualSection
                            ? "gray.100"
                            : "transparent",
                        }}
                        _disabled={{
                          opacity: 0.4,
                          cursor: "not-allowed",
                        }}>
                        Export as Word Document
                      </MenuItem>
                    </Tooltip>
                    <MenuDivider />
                    <Tooltip
                      label={
                        !canEdit
                          ? "You don't have permission to delete this section"
                          : undefined
                      }>
                      <MenuItem
                        isDisabled={!canEdit}
                        onClick={onClickDelete}
                        color="red.600"
                        icon={<DeleteIcon />}
                        border="none"
                        _hover={{
                          bg: canEdit ? "red.50" : "transparent",
                          color: canEdit ? "red.700" : "red.600",
                        }}
                        _focus={{
                          bg: canEdit ? "red.50" : "transparent",
                          color: canEdit ? "red.700" : "red.600",
                        }}
                        _disabled={{
                          opacity: 0.4,
                          cursor: "not-allowed",
                        }}>
                        Delete
                      </MenuItem>
                    </Tooltip>
                  </MenuList>
                </Portal>
              </Menu>
            </ButtonGroup>
          </HStack>
        </Box>

        <Box px="3" py="2" fontSize="1rem" className="manual-rte">
          <EditorContent editor={readonlyEditor} />
        </Box>
      </Box>
    );
  },
);

export default ManualSection;
